import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import ContactFormPopup from '../../../components/feature/ContactFormPopup';

interface NavigationProps {
  scrolled: boolean;
}

export default function Navigation({ scrolled }: NavigationProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isFormPopupOpen, setIsFormPopupOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const handleGetStarted = () => {
    setIsFormPopupOpen(true);
  };

  const handleSignIn = () => {
    window.REACT_APP_NAVIGATE('/login');
  };

  const navigate = (path: string) => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE(path);
    }
    setMobileMenuOpen(false);
  };

  const handleNavClick = (path: string, sectionId?: string) => {
    if (path === '/') {
      if (sectionId) {
        navigate('/');
        setTimeout(() => scrollToSection(sectionId), 100);
      } else {
        navigate('/');
      }
    } else if (path) {
      navigate(path);
    } else if (sectionId) {
      const currentPath = window.location.pathname;
      if (currentPath === '/') {
        scrollToSection(sectionId);
      } else {
        navigate('/');
        setTimeout(() => scrollToSection(sectionId), 100);
      }
    }
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center gap-4 lg:gap-6 flex-1">
              <button
                onClick={() => handleNavClick('/')}
                className="bg-white border-4 border-orange-500 text-orange-500 px-6 lg:px-8 py-3 rounded-full text-lg lg:text-xl font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                Home
              </button>
              <button
                onClick={() => handleNavClick('/', 'pricing')}
                className="bg-white border-4 border-blue-600 text-blue-600 px-6 lg:px-8 py-3 rounded-full text-lg lg:text-xl font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                Pricing
              </button>
              <button
                onClick={() => handleNavClick('/', 'about')}
                className="bg-white border-4 border-yellow-500 text-yellow-600 px-6 lg:px-8 py-3 rounded-full text-lg lg:text-xl font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                About
              </button>
              <button
                onClick={() => handleNavClick('/reviews')}
                className="bg-white border-4 border-purple-500 text-purple-600 px-6 lg:px-8 py-3 rounded-full text-lg lg:text-xl font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                Reviews
              </button>
              <button
                onClick={() => handleNavClick('/gallery')}
                className="bg-white border-4 border-pink-500 text-pink-600 px-6 lg:px-8 py-3 rounded-full text-lg lg:text-xl font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                Gallery
              </button>
              <button
                onClick={() => handleNavClick('/account')}
                className="bg-white border-4 border-green-500 text-green-600 px-6 lg:px-8 py-3 rounded-full text-lg lg:text-xl font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                My Account
              </button>
            </div>
            <button
              onClick={handleGetStarted}
              className="bg-teal-500 border-4 border-white text-white px-6 lg:px-8 py-3 rounded-full text-lg lg:text-xl font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer ml-8"
            >
              Get Started
            </button>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center justify-between">
            <div className="text-xl font-bold text-blue-600" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
              Can-do Crew
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="bg-white border-4 border-red-500 text-red-500 p-3 rounded-full shadow-lg cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="md:hidden mt-4 bg-white rounded-3xl shadow-xl p-4 space-y-3"
            >
              <button
                onClick={() => handleNavClick('/')}
                className="w-full bg-white border-4 border-orange-500 text-orange-500 px-6 py-3 rounded-full text-lg font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                Home
              </button>
              <button
                onClick={() => handleNavClick('/', 'pricing')}
                className="w-full bg-white border-4 border-blue-600 text-blue-600 px-6 py-3 rounded-full text-lg font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                Pricing
              </button>
              <button
                onClick={() => handleNavClick('/', 'about')}
                className="w-full bg-white border-4 border-yellow-500 text-yellow-600 px-6 py-3 rounded-full text-lg font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                About
              </button>
              <button
                onClick={() => handleNavClick('/reviews')}
                className="w-full bg-white border-4 border-purple-500 text-purple-600 px-6 py-3 rounded-full text-lg font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                Reviews
              </button>
              <button
                onClick={() => handleNavClick('/gallery')}
                className="w-full bg-white border-4 border-pink-500 text-pink-600 px-6 py-3 rounded-full text-lg font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                Gallery
              </button>
              <button
                onClick={() => handleNavClick('/account')}
                className="w-full bg-white border-4 border-green-500 text-green-600 px-6 py-3 rounded-full text-lg font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                My Account
              </button>
              <button
                onClick={handleGetStarted}
                className="w-full bg-teal-500 border-4 border-white text-white px-6 py-3 rounded-full text-lg font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                Get Started
              </button>
              <button
                onClick={() => handleNavClick('/account')}
                className="w-full text-sm text-gray-700 hover:text-teal-600 font-semibold cursor-pointer transition-colors underline py-2"
              >
                Sign In
              </button>
            </motion.div>
          )}
        </div>
      </motion.nav>
      {/* Contact Form Popup */}
      <ContactFormPopup
        isOpen={isFormPopupOpen}
        onClose={() => setIsFormPopupOpen(false)}
      />
    </>
  );
}
